import api from '../../api/testFlow';

// Application state
const state = () => ({
    test: null,
    token: null,
    userAnswers: {},
    currentTestHash: null,
    uidToken: null,
    isDemo: false,
    isFinished: false
});

// Getters
const getters = {
    test: state => {
        return state.test;
    },
    generalStep: state => {
        return (state.currentTestHash && state.userAnswers[state.currentTestHash]) ? state.userAnswers[state.currentTestHash].steps.general : 1;
    },
    areaStep: state => {
        return (state.currentTestHash && state.userAnswers[state.currentTestHash]) ? state.userAnswers[state.currentTestHash].steps.area : 0;
    },
    questionStep: state => {
        return (state.currentTestHash && state.userAnswers[state.currentTestHash]) ? state.userAnswers[state.currentTestHash].steps.question : 0;
    },
    generalQuestionStep: state => {
        return (state.currentTestHash && state.userAnswers[state.currentTestHash]) ? state.userAnswers[state.currentTestHash].steps.generalQuestion : 0;
    },
    areasLength: state => {
        return (state.currentTestHash && state.userAnswers[state.currentTestHash]) ? state.userAnswers[state.currentTestHash].areas.length : 0;
    },
    questionsLength: state => {
        return (state.currentTestHash && state.userAnswers[state.currentTestHash]) ? state.userAnswers[state.currentTestHash].areas[0].questions.length : 0;
    },
    userAnswers: state => {
        return (state.currentTestHash && state.userAnswers[state.currentTestHash]) ? state.userAnswers[state.currentTestHash] : {};
    },
    userAge: state => {
        return (state.currentTestHash && state.userAnswers[state.currentTestHash]) ? state.userAnswers[state.currentTestHash].user.age : null;
    },
    userUUID: state => {
        return (state.currentTestHash && state.userAnswers[state.currentTestHash]) ? state.userAnswers[state.currentTestHash].user.uuid : null;
    },
    userName: state => {
        return (state.currentTestHash && state.userAnswers[state.currentTestHash]) ? state.userAnswers[state.currentTestHash].user.name : null;
    },
    areas: state => {
        return (state.currentTestHash && state.userAnswers[state.currentTestHash]) ? state.userAnswers[state.currentTestHash].areas : null;
    },
    areaAnswer: state => (areaStep, questionStep) => {
        return (state.currentTestHash && state.userAnswers[state.currentTestHash]) ? state.userAnswers[state.currentTestHash].areas[areaStep].questions[questionStep].userAnswer : null;
    },
    generalQuestions: state => {
        return (state.currentTestHash && state.userAnswers[state.currentTestHash]) ? state.userAnswers[state.currentTestHash].generalQuestions : null;
    },
    generalQuestionAnswer: state => (currentQuestion) => {
        return (state.currentTestHash && state.userAnswers[state.currentTestHash]) ? state.userAnswers[state.currentTestHash].generalQuestions[currentQuestion].userAnswer : null;
    },
    isDemo: state => {
        return state.isDemo;
    },
    isFinished: state => {
        return state[state.currentTestHash].isFinished;
    },
    verificationCode: state => {
        return state.userAnswers[state.currentTestHash].verificationCode;
    },
    issetGeneralQuestions: state => {
        return state.userAnswers[state.currentTestHash].issetGeneralQuestions;
    }
};

// Actions
const actions = {
    getApiData(context, hash, isDemo) {
        context.commit('setIsLoading', true, {root: true});
        context.commit('setCurrentTestHash', hash);

        return api.getTest(hash + (isDemo ? '$DM' : '')).then(data => {
            if (!data.questions.length && !data.generalQuestions.length) {
                return Promise.resolve({
                    testConfigFailed: true
                });
            }

            if (context.state.isDemo === true) {
                context.commit('resetUserAnswers');
            }

            context.commit('setTest', data.test);
            if (!context.state.userAnswers || !context.state.userAnswers[hash]) {
                context.commit('setUserAnswers', data);
            }

            if (context.state.isDemo === true && context.getters.generalStep === 1) {
                context.commit('generalStepIncrement');
            }

            return Promise.resolve(data);
        }).catch(error => {
            return Promise.reject(error);
        }).finally(() => {
            context.commit('setIsLoading', false, {root: true});
        });
    },
    getVerificationCode(context, data) {
        context.commit('setIsLoading', true, {root: true});
        context.commit('setFinishTest');

        return api.getVerificationCode(data).catch(error => {
            return Promise.reject(error);
        }).finally(() => {
            context.commit('setIsLoading', false, { root: true });
        });
    },
    saveUserAnswer({commit, state, dispatch}, data) {
        if (state.isDemo) {
            data.isDemo = state.isDemo;
        }

        return api.store(data).then(response => {
            commit('setUserUUID', response.tester.uuid);
            if (response.answer.area_uuid === 'general') {
                commit('setGeneralAnswerData', response);
            } else {
                commit('setAreaAnswerData', response);
            }

            return Promise.resolve(response);
        }).catch(error => {
            return Promise.reject(error, true);
        });
    },
    createTester(context, data) {
        context.commit('setIsLoading', true, { root: true });

        return api.createTester(data).catch(error => {
            return Promise.reject(error);
        }).finally(() => {
            context.commit('setIsLoading', false, { root: true });
        });
    }
};

// Setters
const mutations = {
    setTest: (state, data) => {
        state.test = data;
    },
    setUserAnswers: (state, data) => {
        let newData = {
            areas: [],
            isFinished: false,
            issetQuestions: false,
            issetGeneralQuestions: false,
            verificationCode: null,
            generalQuestions: [],
            user: {
                age: null,
                name: null,
                uuid: null
            },
            steps: {
                general: 1,
                area: 0,
                question: 0,
                generalQuestion: 0
            }
        };

        // Set questions fore areas
        if (data.questions.length) {
            newData.issetQuestions = true;
            data.test.areas.forEach((area, areaKey) => {
                newData.areas.push({
                    areaData: area,
                    questions: data.questions
                });
                newData.areas[areaKey].questions.forEach((question, questionKey) => {
                    newData.areas[areaKey].questions[questionKey].userAnswer = null;
                    newData.areas[areaKey].questions[questionKey].userAnswerData = null;
                });
            });
        }

        // Set general questions
        if (data.generalQuestions.length) {
            newData.issetGeneralQuestions = true;
            newData.generalQuestions = data.generalQuestions;
            newData.generalQuestions.forEach((question, key) => {
                data.generalQuestions[key].userAnswer = null;
                data.generalQuestions[key].userAnswerData = null;
            });
        }

        state.userAnswers = {...state.userAnswers, [data.test.hash]: JSON.parse(JSON.stringify(newData))};
    },
    resetUserAnswers: (state, data) => {
        // state.userAnswers = {};
    },
    setVerificationCode(state, code) {
        state.userAnswers[state.currentTestHash].verificationCode = code;
    },
    setQuestionStep(state, step) {
        state.userAnswers[state.currentTestHash].steps.question = step;
    },
    generalStepIncrement(state) {
        let incrementStep = state.userAnswers[state.currentTestHash].steps.general;

        if (incrementStep === 1 && !state.userAnswers[state.currentTestHash].issetQuestions) {
            incrementStep = incrementStep + 2;
        } else if (incrementStep === 2 && !state.userAnswers[state.currentTestHash].issetGeneralQuestions) {
            state.userAnswers[state.currentTestHash].isFinished = true;
        } else {
            incrementStep = ++incrementStep;
        }

        state.userAnswers[state.currentTestHash].steps.general = incrementStep;
    },
    generalStepDecrement(state) {
        state.userAnswers[state.currentTestHash].steps.general = --state.userAnswers[state.currentTestHash].steps.general;
    },
    generalQuestionStepIncrement(state) {
        state.userAnswers[state.currentTestHash].steps.question = ++state.userAnswers[state.currentTestHash].steps.generalQuestion;
    },
    generalQuestionStepDecrement(state) {
        state.userAnswers[state.currentTestHash].steps.question = --state.userAnswers[state.currentTestHash].steps.generalQuestion;
    },
    setUserAge(state, age) {
        state.userAnswers[state.currentTestHash].user.age = age;
    },
    setUserName(state, name) {
        state.userAnswers[state.currentTestHash].user.name = name;
    },
    setUserUUID(state, uuid) {
        state.userAnswers[state.currentTestHash].user.uuid = uuid;
    },
    setCurrentTestHash(state, hash) {
        state.currentTestHash = hash;
    },
    setAreaAnswer(state, data) {
        state.userAnswers[state.currentTestHash].areas[data.areaStep].questions[data.questionStep].userAnswer = data.answer;
    },
    setAreaAnswerData(state, data) {
        state.userAnswers[state.currentTestHash].areas[getters.areaStep(state)].questions[getters.questionStep(state)].userAnswerData = data.answer;

        if ((getters.questionStep(state) + 1) === getters.questionsLength(state)) {
            if ((getters.areaStep(state) + 1) >= getters.areasLength(state)) {
                return state.userAnswers[state.currentTestHash].steps.general = ++state.userAnswers[state.currentTestHash].steps.general;
            }

            state.userAnswers[state.currentTestHash].steps.question = 0;
            state.userAnswers[state.currentTestHash].steps.area = ++state.userAnswers[state.currentTestHash].steps.area;
        } else {
            state.userAnswers[state.currentTestHash].steps.question = ++state.userAnswers[state.currentTestHash].steps.question;
        }
    },
    questionsBack(state) {
        if (getters.questionStep(state) === 0 && getters.areaStep(state) >= 1) {
            state.userAnswers[state.currentTestHash].steps.area = --state.userAnswers[state.currentTestHash].steps.area;
            state.userAnswers[state.currentTestHash].steps.question = getters.questionsLength(state) - 1;
        } else {
            state.userAnswers[state.currentTestHash].steps.question = --state.userAnswers[state.currentTestHash].steps.question;
        }

    },
    generalQuestionsBack(state) {
        if (getters.generalQuestionStep(state) === 0) {
            return state.userAnswers[state.currentTestHash].steps.general = --state.userAnswers[state.currentTestHash].steps.general;
        }

        return state.userAnswers[state.currentTestHash].steps.generalQuestion = --state.userAnswers[state.currentTestHash].steps.generalQuestion;
    },
    setGeneralQuestionAnswer(state, answer) {
        state.userAnswers[state.currentTestHash].generalQuestions[getters.generalQuestionStep(state)].userAnswer = answer;
    },
    setGeneralAnswerData(state, data) {
        state.userAnswers[state.currentTestHash].generalQuestions[getters.generalQuestionStep(state)].userAnswerData = data.answer;

        if (getters.generalQuestionStep(state) + 1 === getters.generalQuestions(state).length) {
            state.userAnswers[state.currentTestHash].steps.general = ++state.userAnswers[state.currentTestHash].steps.general;
        }

        return state.userAnswers[state.currentTestHash].steps.generalQuestion = ++state.userAnswers[state.currentTestHash].steps.generalQuestion;
    },
    setFinishTest(state) {
        state.userAnswers[state.currentTestHash].isFinished = true;
    },
    setDemoTest(state, value) {
        state.isDemo = value;
    }
};

// Export module
export default {
    namespaced: true,

    state,
    actions,
    getters,
    mutations
}
